import { useState } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Badge } from "components/ui/badge";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Plus,
  X,
  Calculator,
  Robot,
  CaretDown,
  CaretRight,
  ArrowUp,
  ArrowDown,
  Trash,
  Info,
} from "@phosphor-icons/react";
import { ConditionGroupEditor } from "./ConditionGroupEditor";
import {
  FieldExtractionRule,
  CalculationRule,
  AIFallbackConfig,
  ExtractionConditionGroup,
  TIME_UNIT_LABELS,
  AI_PROMPT_PLACEHOLDERS,
} from "./types";

interface ExtractionRuleEditorProps {
  rule: FieldExtractionRule;
  index: number;
  totalRules: number;
  onUpdate: (rule: FieldExtractionRule) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

/**
 * ExtractionRuleEditor - Edits a single extraction rule with calculation and conditions
 */
export const ExtractionRuleEditor = ({
  rule,
  index,
  totalRules,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
}: ExtractionRuleEditorProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const updateCalculationRule = (updates: Partial<CalculationRule>) => {
    onUpdate({
      ...rule,
      calculationRule: {
        ...rule.calculationRule,
        baseField: rule.calculationRule?.baseField || 'documentDate',
        operation: rule.calculationRule?.operation || 'add',
        amount: rule.calculationRule?.amount || 10,
        unit: rule.calculationRule?.unit || 'years',
        ...updates,
      },
    });
  };

  const updateConditions = (conditions: ExtractionConditionGroup | undefined) => {
    onUpdate({
      ...rule,
      calculationRule: rule.calculationRule
        ? { ...rule.calculationRule, conditions }
        : undefined,
    });
  };

  const addConditions = () => {
    updateCalculationRule({
      conditions: {
        logic: 'AND',
        conditions: [{ field: '', operator: '==', value: '' }],
      },
    });
  };

  const removeConditions = () => {
    if (rule.calculationRule) {
      const { conditions, ...rest } = rule.calculationRule;
      onUpdate({
        ...rule,
        calculationRule: rest,
      });
    }
  };

  const hasConditions = !!rule.calculationRule?.conditions;

  // Generate a description for the rule
  const getRuleDescription = (): string => {
    if (!rule.calculationRule) return 'Geen berekening';
    const calc = rule.calculationRule;
    const baseLabel = calc.baseField === 'documentDate' ? 'documentdatum' : 'vandaag';
    const opLabel = calc.operation === 'add' ? '+' : '-';
    const unitLabel = TIME_UNIT_LABELS[calc.unit];
    return `${baseLabel} ${opLabel} ${calc.amount} ${unitLabel}`;
  };

  return (
    <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
      {/* Rule Header */}
      <div
        className="flex items-center gap-2 p-3 bg-neutral-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2 flex-1">
          {isExpanded ? (
            <CaretDown className="h-4 w-4 text-neutral-400" />
          ) : (
            <CaretRight className="h-4 w-4 text-neutral-400" />
          )}
          <Badge variant="outline" className="text-xs">
            #{index + 1}
          </Badge>
          <span className="text-sm font-medium">
            {rule.name || `Regel ${index + 1}`}
          </span>
          <span className="text-xs text-neutral-500">
            — {getRuleDescription()}
          </span>
          {hasConditions && (
            <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700">
              Voorwaardelijk
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {onMoveUp && index > 0 && (
            <Button variant="ghost" size="sm" onClick={onMoveUp} className="h-7 w-7 p-0">
              <ArrowUp className="h-4 w-4" />
            </Button>
          )}
          {onMoveDown && index < totalRules - 1 && (
            <Button variant="ghost" size="sm" onClick={onMoveDown} className="h-7 w-7 p-0">
              <ArrowDown className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Rule Content */}
      {isExpanded && (
        <div className="p-3 space-y-4 border-t">
          {/* Rule Name */}
          <div className="space-y-1">
            <Label className="text-xs">Regel naam (optioneel)</Label>
            <Input
              value={rule.name || ''}
              onChange={(e) => onUpdate({ ...rule, name: e.target.value })}
              placeholder="bijv. Vlaanderen - 10 jaar geldig"
              className="h-8 text-sm"
            />
          </div>

          {/* Calculation Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-emerald-600" weight="duotone" />
              <Label className="text-sm font-medium">Berekening</Label>
            </div>

            <div className="flex items-center gap-2 flex-wrap bg-emerald-50 p-3 rounded-md">
              {/* Base Field */}
              <Select
                value={rule.calculationRule?.baseField || 'documentDate'}
                onValueChange={(value) =>
                  updateCalculationRule({ baseField: value as 'documentDate' | 'now' })
                }
              >
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="documentDate">Documentdatum</SelectItem>
                  <SelectItem value="now">Vandaag</SelectItem>
                </SelectContent>
              </Select>

              {/* Operation */}
              <Select
                value={rule.calculationRule?.operation || 'add'}
                onValueChange={(value) =>
                  updateCalculationRule({ operation: value as 'add' | 'subtract' })
                }
              >
                <SelectTrigger className="h-8 w-[80px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">+</SelectItem>
                  <SelectItem value="subtract">-</SelectItem>
                </SelectContent>
              </Select>

              {/* Amount */}
              <Input
                type="number"
                value={rule.calculationRule?.amount || 10}
                onChange={(e) =>
                  updateCalculationRule({ amount: parseInt(e.target.value) || 0 })
                }
                className="h-8 w-[70px] text-xs"
                min={0}
              />

              {/* Unit */}
              <Select
                value={rule.calculationRule?.unit || 'years'}
                onValueChange={(value) =>
                  updateCalculationRule({ unit: value as 'days' | 'months' | 'years' })
                }
              >
                <SelectTrigger className="h-8 w-[100px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIME_UNIT_LABELS).map(([unit, label]) => (
                    <SelectItem key={unit} value={unit}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Conditions Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-violet-600" weight="duotone" />
                <Label className="text-sm font-medium">Voorwaarden (optioneel)</Label>
              </div>
              {!hasConditions ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addConditions}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Voorwaarden toevoegen
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeConditions}
                  className="h-7 text-xs text-red-500 hover:text-red-700"
                >
                  <X className="h-3 w-3 mr-1" />
                  Voorwaarden verwijderen
                </Button>
              )}
            </div>

            {hasConditions && rule.calculationRule?.conditions && (
              <ConditionGroupEditor
                group={rule.calculationRule.conditions}
                onChange={(group) => updateConditions(group)}
                isRoot={true}
              />
            )}

            {!hasConditions && (
              <p className="text-xs text-neutral-500 bg-neutral-50 p-2 rounded">
                Zonder voorwaarden wordt deze regel altijd toegepast (als eerste overeenkomende regel).
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AIFallbackEditorProps {
  config: AIFallbackConfig | undefined;
  onChange: (config: AIFallbackConfig | undefined) => void;
}

/**
 * AIFallbackEditor - Edits AI fallback configuration
 */
export const AIFallbackEditor = ({ config, onChange }: AIFallbackEditorProps) => {
  const isEnabled = config?.enabled ?? false;

  const toggleEnabled = () => {
    if (isEnabled) {
      onChange(undefined);
    } else {
      onChange({
        enabled: true,
        customPrompt: '',
      });
    }
  };

  const updatePrompt = (prompt: string) => {
    onChange({
      ...config,
      enabled: true,
      customPrompt: prompt,
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Checkbox
          id="ai-fallback"
          checked={isEnabled}
          onCheckedChange={toggleEnabled}
        />
        <label htmlFor="ai-fallback" className="flex items-center gap-2 cursor-pointer">
          <Robot className="h-4 w-4 text-blue-600" weight="duotone" />
          <span className="text-sm font-medium">AI Fallback</span>
          <span className="text-xs text-neutral-500">
            — wanneer geen regel van toepassing is
          </span>
        </label>
      </div>

      {isEnabled && (
        <div className="pl-6 space-y-2">
          <Label className="text-xs text-neutral-500">
            Aangepaste prompt (optioneel)
          </Label>
          <Textarea
            value={config?.customPrompt || ''}
            onChange={(e) => updatePrompt(e.target.value)}
            placeholder="bijv. Wat is de geldigheidsduur van {label} in {address}?"
            rows={2}
            className="text-sm resize-none"
          />
          <div className="text-xs text-neutral-500 bg-blue-50 p-2 rounded">
            <strong>Beschikbare placeholders:</strong>
            <ul className="mt-1 space-y-0.5">
              {AI_PROMPT_PLACEHOLDERS.map((p) => (
                <li key={p.key}>
                  <code className="text-blue-700">{p.key}</code> — {p.description}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractionRuleEditor;
